import React, { useState } from 'react';
import { ViewLayout, StatusBadge, Button, Card, CategoryDropdown } from '../ui';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  Sun, Moon, Check, X, Clock, AlertCircle, XCircle, 
  CheckCircle, Star, Heart, ShoppingCart, Truck, Plus, Edit, 
  Trash2, Search, Filter, Eye, Package, Sparkles, RefreshCw
} from 'lucide-react';

const UISetupView = () => {
  const { theme, resolvedTheme, isDark, isLight } = useTheme();
  const [showColorValues, setShowColorValues] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');

  // Color Palettes for Documentation
  const colorPalettes = {
    primary: {
      name: 'Primary (Blue)',
      colors: [
        { name: 'blue-100', class: 'bg-blue-100', textClass: 'text-blue-900' },
        { name: 'blue-400', class: 'bg-blue-400', textClass: 'text-white' },
        { name: 'blue-600', class: 'bg-blue-600', textClass: 'text-white' },
        { name: 'blue-800', class: 'bg-blue-800', textClass: 'text-white' },
      ]
    },
    success: {
      name: 'Success (Green)',
      colors: [
        { name: 'green-100', class: 'bg-green-100', textClass: 'text-green-900' },
        { name: 'green-400', class: 'bg-green-400', textClass: 'text-white' },
        { name: 'green-600', class: 'bg-green-600', textClass: 'text-white' },
        { name: 'green-800', class: 'bg-green-800', textClass: 'text-white' },
      ]
    },
    warning: {
      name: 'Warning (Orange)',
      colors: [
        { name: 'orange-100', class: 'bg-orange-100', textClass: 'text-orange-900' },
        { name: 'orange-400', class: 'bg-orange-400', textClass: 'text-white' },
        { name: 'orange-600', class: 'bg-orange-600', textClass: 'text-white' },
        { name: 'orange-800', class: 'bg-orange-800', textClass: 'text-white' },
      ]
    },
    danger: {
      name: 'Danger (Red)',
      colors: [
        { name: 'red-100', class: 'bg-red-100', textClass: 'text-red-900' },
        { name: 'red-400', class: 'bg-red-400', textClass: 'text-white' },
        { name: 'red-600', class: 'bg-red-600', textClass: 'text-white' },
        { name: 'red-800', class: 'bg-red-800', textClass: 'text-white' },
      ]
    },
    info: {
      name: 'Info (Purple)',
      colors: [
        { name: 'purple-100', class: 'bg-purple-100', textClass: 'text-purple-900' },
        { name: 'purple-400', class: 'bg-purple-400', textClass: 'text-white' },
        { name: 'purple-600', class: 'bg-purple-600', textClass: 'text-white' },
        { name: 'purple-800', class: 'bg-purple-800', textClass: 'text-white' },
      ]
    },
    neutral: {
      name: 'Neutral (Gray)',
      colors: [
        { name: 'gray-100', class: 'bg-gray-100', textClass: 'text-gray-900' },
        { name: 'gray-400', class: 'bg-gray-400', textClass: 'text-white' },
        { name: 'gray-600', class: 'bg-gray-600', textClass: 'text-white' },
        { name: 'gray-800', class: 'bg-gray-800', textClass: 'text-white' },
      ]
    }
  };

  const stats = [
    { label: 'Theme Mode', value: theme, color: 'blue' },
    { label: 'Resolved Theme', value: resolvedTheme, color: isDark ? 'purple' : 'orange' },
    { label: 'Is Dark Mode', value: isDark ? 'Yes' : 'No', color: isDark ? 'green' : 'red' },
    { label: 'System Preference', value: 'Disabled', color: 'gray' }
  ].map((stat, index) => (
    <ViewLayout.Stat key={index} title={stat.label} value={stat.value} color={stat.color} />
  ));

  return (
    <ViewLayout
      title="UI Setup & Style Guide"
      subtitle="Development view for testing colors, themes, and UI components"
      stats={stats}
      actions={[
        <button
          key="toggle-values"
          onClick={() => setShowColorValues(!showColorValues)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          {showColorValues ? 'Hide' : 'Show'} Color Values
        </button>
      ]}
    >
      <div className="space-y-6">
        
        {/* Theme Information */}
        <section className="bg-white dark:bg-gray-800 rounded-lg border p-4">
          <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">Theme Information</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
              <div className="font-medium text-gray-900 dark:text-white text-xs">Current Theme</div>
              <div className="text-blue-600 dark:text-blue-400 text-sm">{theme}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
              <div className="font-medium text-gray-900 dark:text-white text-xs">Resolved Theme</div>
              <div className="text-blue-600 dark:text-blue-400 text-sm">{resolvedTheme}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
              <div className="font-medium text-gray-900 dark:text-white text-xs">Data Attribute</div>
              <div className="text-blue-600 dark:text-blue-400 text-sm">data-theme="{resolvedTheme}"</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded">
              <div className="font-medium text-gray-900 dark:text-white text-xs">System Preference</div>
              <div className="text-blue-600 dark:text-blue-400 text-sm">
                System detection disabled
              </div>
            </div>
          </div>
        </section>

        {/* Status Badges */}
        <section className="bg-white dark:bg-gray-800 rounded-lg border p-4">
          <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">Status Badges</h3>
          <div className="flex flex-wrap gap-4">
            <div className="text-center space-y-1">
              <StatusBadge status="available" showIcon={true} />
              <div className="text-xs text-gray-600 dark:text-gray-400">Available</div>
            </div>
            <div className="text-center space-y-1">
              <StatusBadge status="checked_out" showIcon={true} />
              <div className="text-xs text-gray-600 dark:text-gray-400">Checked Out</div>
            </div>
            <div className="text-center space-y-1">
              <StatusBadge status="reserved" showIcon={true} />
              <div className="text-xs text-gray-600 dark:text-gray-400">Reserved</div>
            </div>
            <div className="text-center space-y-1">
              <StatusBadge status="unavailable" showIcon={true} />
              <div className="text-xs text-gray-600 dark:text-gray-400">Unavailable</div>
            </div>
          </div>
          {showColorValues && (
            <div className="mt-3 bg-gray-50 dark:bg-gray-700 p-3 rounded text-sm">
              <div className="font-medium mb-1 text-gray-900 dark:text-white text-xs">Current Status Badge Colors ({resolvedTheme} mode):</div>
              <div className="space-y-1 font-mono text-xs text-gray-700 dark:text-gray-300">
                <div>Available: {isDark ? 'bg-green-800 text-green-100' : 'bg-green-100 text-green-800'}</div>
                <div>Checked Out: {isDark ? 'bg-orange-800 text-orange-100' : 'bg-orange-100 text-orange-800'}</div>
                <div>Reserved: {isDark ? 'bg-purple-800 text-purple-100' : 'bg-purple-100 text-purple-800'}</div>
                <div>Unavailable: {isDark ? 'bg-red-800 text-red-100' : 'bg-red-100 text-red-800'}</div>
              </div>
            </div>
          )}
        </section>

        {/* Buttons */}
        <section className="bg-white dark:bg-gray-800 rounded-lg border p-4">
          <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">Button Variants</h3>
          
          <div className="space-y-4">
            {/* Standard Buttons */}
            <div>
              <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Standard Buttons</h4>
              <div className="flex flex-wrap gap-2">
                <Button variant="primary" size="compact">Primary</Button>
                <Button variant="secondary" size="compact">Secondary</Button>
                <Button variant="success" size="compact">Success</Button>
                <Button variant="warning" size="compact">Warning</Button>
                <Button variant="danger" size="compact">Danger</Button>
                <Button variant="nav" size="compact">Nav</Button>
              </div>
            </div>
            
            {/* Gradient Buttons */}
            <div>
              <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Gradient Buttons (from Search)</h4>
              <div className="flex flex-wrap gap-2">
                <button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-2 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all text-sm">
                  Purple to Pink
                </button>
                <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all text-sm">
                  Blue to Purple  
                </button>
                <button className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-3 py-2 rounded-lg hover:from-green-700 hover:to-blue-700 transition-all text-sm">
                  Green to Blue
                </button>
              </div>
              {showColorValues && (
                <div className="mt-2 text-xs font-mono text-gray-600 dark:text-gray-400">
                  bg-gradient-to-r from-purple-600 to-pink-600
                </div>
              )}
            </div>
            
            {/* Button Sizes & Icons */}
            <div>
              <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Sizes & Icons</h4>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="primary" size="compact">Compact</Button>
                <Button variant="primary" size="normal">Normal</Button>
                <Button variant="primary" icon={Plus} size="compact">With Icon</Button>
                <Button variant="success" icon={Check} size="compact">Approve</Button>
              </div>
            </div>
          </div>
        </section>

        {/* Form Elements */}
        <section className="bg-white dark:bg-gray-800 rounded-lg border p-4">
          <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">Form Elements</h3>
          
          <div className="space-y-4">
            {/* Search Bar */}
            <div>
              <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Search Bar</h4>
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="Search props..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            {/* Category Dropdown Demo */}
            <div>
              <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Category Dropdown (Hierarchical)</h4>
              <div className="max-w-xs">
                <CategoryDropdown
                  value={selectedCategory}
                  onChange={(category) => setSelectedCategory(category)}
                  showAllOption={true}
                  allOptionLabel="All Categories"
                  placeholder="Select Category"
                />
                {selectedCategory && (
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Selected: <span className="font-medium text-blue-600 dark:text-blue-400">{selectedCategory}</span>
                  </div>
                )}
              </div>
              {showColorValues && (
                <div className="mt-2 text-xs font-mono text-gray-600 dark:text-gray-400">
                  Dynamic hierarchical category dropdown with accordion-style expansion
                </div>
              )}
            </div>
            
            {/* Pagination */}
            <div>
              <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Pagination</h4>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 text-sm">
                  Previous
                </button>
                <button className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                  1
                </button>
                <button className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 text-sm">
                  2
                </button>
                <button className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 text-sm">
                  Next
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Colorful Icons */}
        <section className="bg-white dark:bg-gray-800 rounded-lg border p-4">
          <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">Colorful Icons (from Search Views)</h3>
          <div className="flex flex-wrap gap-4">
            <div className="text-center space-y-1">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-lg flex items-center justify-center">
                <Search className="h-5 w-5 text-blue-600 dark:text-blue-300" />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Search</div>
            </div>
            <div className="text-center space-y-1">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-800 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-300" />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Available</div>
            </div>
            <div className="text-center space-y-1">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-800 rounded-lg flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-300" />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Magic</div>
            </div>
            <div className="text-center space-y-1">
              <div className="w-8 h-8 bg-orange-100 dark:bg-orange-800 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Time</div>
            </div>
            <div className="text-center space-y-1">
              <div className="w-8 h-8 bg-pink-100 dark:bg-pink-800 rounded-lg flex items-center justify-center">
                <Heart className="h-5 w-5 text-pink-600 dark:text-pink-300" />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Favorite</div>
            </div>
            <div className="text-center space-y-1">
              <div className="w-8 h-8 bg-red-100 dark:bg-red-800 rounded-lg flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-300" />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Error</div>
            </div>
          </div>
          {showColorValues && (
            <div className="mt-2 text-xs font-mono text-gray-600 dark:text-gray-400">
              bg-blue-100 dark:bg-blue-800 + text-blue-600 dark:text-blue-300
            </div>
          )}
        </section>

        {/* Object Strokes & Borders */}
        <section className="bg-white dark:bg-gray-800 rounded-lg border p-4">
          <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">Strokes & Borders</h3>
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Border Styles</h4>
              <div className="flex flex-wrap gap-3">
                <div className="w-16 h-12 border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700 flex items-center justify-center text-xs">Default</div>
                <div className="w-16 h-12 border-2 border-blue-300 dark:border-blue-600 rounded bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-xs">Accent</div>
                <div className="w-16 h-12 border border-dashed border-gray-400 dark:border-gray-500 rounded bg-gray-50 dark:bg-gray-700 flex items-center justify-center text-xs">Dashed</div>
                <div className="w-16 h-12 ring-2 ring-blue-500 ring-opacity-50 rounded bg-white dark:bg-gray-800 flex items-center justify-center text-xs">Focus</div>
              </div>
              {showColorValues && (
                <div className="mt-2 space-y-1 text-xs font-mono text-gray-600 dark:text-gray-400">
                  <div>Default: border-gray-200 dark:border-gray-600</div>
                  <div>Accent: border-blue-300 dark:border-blue-600</div>
                  <div>Focus: ring-2 ring-blue-500 ring-opacity-50</div>
                </div>
              )}
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Card Shadows</h4>
              <div className="flex flex-wrap gap-3">
                <div className="w-16 h-12 shadow-sm border rounded bg-white dark:bg-gray-800 flex items-center justify-center text-xs">SM</div>
                <div className="w-16 h-12 shadow border rounded bg-white dark:bg-gray-800 flex items-center justify-center text-xs">MD</div>
                <div className="w-16 h-12 shadow-lg border rounded bg-white dark:bg-gray-800 flex items-center justify-center text-xs">LG</div>
                <div className="w-16 h-12 shadow-xl border rounded bg-white dark:bg-gray-800 flex items-center justify-center text-xs">XL</div>
              </div>
            </div>
          </div>
        </section>

        {/* Color Palettes */}
        <section className="bg-white dark:bg-gray-800 rounded-lg border p-4">
          <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">Color Palettes</h3>
          <div className="space-y-4">
            {Object.entries(colorPalettes).map(([key, palette]) => (
              <div key={key}>
                <h4 className="font-medium mb-2 text-gray-900 dark:text-white text-sm">{palette.name}</h4>
                <div className="grid grid-cols-4 gap-2">
                  {palette.colors.map((color) => (
                    <div key={color.name} className="text-center">
                      <div 
                        className={`${color.class} ${color.textClass} h-12 rounded flex items-center justify-center text-xs font-medium border border-gray-200 dark:border-gray-600`}
                      >
                        {color.name.split('-')[1]}
                      </div>
                      {showColorValues && (
                        <div className="text-xs mt-1 text-gray-600 dark:text-gray-400 font-mono">
                          {color.name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Icons */}
        <section className="bg-white dark:bg-gray-800 rounded-lg border p-4">
          <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">Common Icons</h3>
          <div className="grid grid-cols-8 md:grid-cols-12 lg:grid-cols-16 gap-3">
            {[
              { icon: Search, name: 'Search' },
              { icon: Filter, name: 'Filter' },
              { icon: Eye, name: 'View' },
              { icon: Plus, name: 'Add' },
              { icon: Edit, name: 'Edit' },
              { icon: Trash2, name: 'Delete' },
              { icon: Check, name: 'Check' },
              { icon: X, name: 'Close' },
              { icon: Heart, name: 'Heart' },
              { icon: Star, name: 'Star' },
              { icon: ShoppingCart, name: 'Cart' },
              { icon: Truck, name: 'Truck' },
              { icon: Package, name: 'Package' },
              { icon: Clock, name: 'Clock' },
              { icon: CheckCircle, name: 'Success' },
              { icon: AlertCircle, name: 'Alert' }
            ].map(({ icon: Icon, name }) => (
              <div key={name} className="text-center space-y-1">
                <div className="flex justify-center">
                  <Icon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{name}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section className="bg-white dark:bg-gray-800 rounded-lg border p-4">
          <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">Typography</h3>
          <div className="space-y-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Heading 1 - 2xl font-bold</h1>
              {showColorValues && <div className="text-xs text-gray-500 font-mono">text-2xl font-bold text-gray-900 dark:text-white</div>}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Heading 2 - xl font-semibold</h2>
              {showColorValues && <div className="text-xs text-gray-500 font-mono">text-xl font-semibold text-gray-900 dark:text-white</div>}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Heading 3 - lg font-semibold</h3>
              {showColorValues && <div className="text-xs text-gray-500 font-mono">text-lg font-semibold text-gray-900 dark:text-white</div>}
            </div>
            <div>
              <p className="text-base text-gray-700 dark:text-gray-300">Body text - base text-gray-700 dark:text-gray-300</p>
              {showColorValues && <div className="text-xs text-gray-500 font-mono">text-base text-gray-700 dark:text-gray-300</div>}
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Small text - sm text-gray-600 dark:text-gray-400</p>
              {showColorValues && <div className="text-xs text-gray-500 font-mono">text-sm text-gray-600 dark:text-gray-400</div>}
            </div>
          </div>
        </section>

        {/* Development Notes */}
        <section className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <h3 className="text-base font-semibold mb-2 text-yellow-800 dark:text-yellow-200">Development Notes</h3>
          <div className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
            <p>• This view is for development only and shows all current UI elements and their styling</p>
            <p>• Toggle the theme using the theme toggle in the top navigation to see dark/light mode changes</p>
            <p>• Color values can be toggled on/off using the button in the header</p>
            <p>• All components use the centralized theme context for consistent theming</p>
          </div>
        </section>
      </div>
    </ViewLayout>
  );
};

export default UISetupView;
